import Phaser from "phaser";
import { EXT_RULES } from "@urlcade/contracts";
import {
  RULES,
  activeColliders,
  bodyBounds,
  createMovementState,
  createRushState,
  distance,
  intersects,
  stepMovement,
  stepRush,
  withChallengeSetup,
} from "@urlcade/simulation";
import type {
  ActiveMode,
  ArenaProjection,
  InputFrame,
  MapArtifact,
  MovementState,
  OwnPlayerState,
  PracticeTarget,
  PresentationObject,
  ReplaySample,
  Rect,
  RoomPhase,
  SceneProjection,
  VisibleActor,
} from "@urlcade/contracts";
import type { LoadedMap } from "../services/maps";
import type { VisualQuality } from "../services/preferences";
import { tr } from "../i18n/tr";
import { characterAnimationKey, characterAssets, characterFor, type CharacterId, type CharacterPose } from "./character-assets";

export interface GameHud {
  mode: ActiveMode;
  phase?: RoomPhase;
  elapsedMs: number;
  checkpoint: number;
  penaltyMs: number;
  role?: OwnPlayerState["role"];
  energy?: number;
  disguised?: boolean;
  team?: "red" | "blue";
  stunnedMs?: number;
  actionCooldownMs?: number;
  burstCooldownMs?: number;
  arena?: ArenaProjection;
  announcement?: string;
}

export interface LocalRushResult {
  mapTitle: string;
  artifactHash: string;
  simulationVersion: string;
  elapsedMs: number;
  penaltyMs: number;
  checkpoint: number;
  finished: boolean;
}

export interface GameSessionOptions {
  container: HTMLElement;
  map: LoadedMap;
  mode: ActiveMode;
  networked: boolean;
  reducedMotion: boolean;
  quality: VisualQuality;
  challengeOptionId?: string | null;
  replaySamples?: ReplaySample[];
  replayNickname?: string;
  characterId?: CharacterId;
  onHud(hud: GameHud): void;
  onLocalFinish(result: LocalRushResult): void;
  onInput(frames: InputFrame[]): void;
  onAction(action: GameAction, payload?: GameActionPayload): void;
}

type GameAction = "select_prop" | "toggle_disguise" | "scan" | "push_attack" | "area_pulse" | "interact" | "drop_carried";
type GameActionPayload = string | { x: number; y: number } | { direction: -1 | 1; targetId?: string };

interface ActorTrack {
  sprite: Phaser.GameObjects.Container;
  body: Phaser.GameObjects.Graphics;
  avatar: Phaser.GameObjects.Sprite;
  characterId: CharacterId;
  name: Phaser.GameObjects.Text;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  changedAt: number;
}

type GameControl = "left" | "right" | "jump" | "dash";
interface DustParticle { x: number; y: number; vx: number; vy: number; lifeMs: number; maxLifeMs: number; }

function renderScaleForQuality(quality: VisualQuality): number {
  const deviceRatio = Number.isFinite(window.devicePixelRatio) && window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
  if (quality === "economy") return Math.max(0.65, Math.min(0.75, deviceRatio));
  if (quality === "balanced") return Math.max(0.75, Math.min(1, deviceRatio));
  return Math.min(1.75, Math.max(1.25, deviceRatio));
}

function outputSize(container: HTMLElement, renderScale: number): { width: number; height: number } {
  const bounds = container.getBoundingClientRect();
  const width = bounds.width > 0 ? bounds.width : 1280;
  const height = bounds.height > 0 ? bounds.height : 720;
  return { width: Math.max(1, Math.round(width * renderScale)), height: Math.max(1, Math.round(height * renderScale)) };
}

function fitImageInsideDisplayBounds(image: Phaser.GameObjects.Image, width: number, height: number): void {
  const boundWidth = Number.isFinite(width) && width > 0 ? width : 24;
  const boundHeight = Number.isFinite(height) && height > 0 ? height : 36;
  const sourceWidth = image.frame.realWidth;
  const sourceHeight = image.frame.realHeight;
  if (!Number.isFinite(sourceWidth) || sourceWidth <= 0 || !Number.isFinite(sourceHeight) || sourceHeight <= 0) {
    image.setDisplaySize(boundWidth, boundHeight);
    return;
  }

  const scale = Math.min(boundWidth / sourceWidth, boundHeight / sourceHeight);
  const fittedWidth = sourceWidth * scale;
  const fittedHeight = sourceHeight * scale;
  if (!Number.isFinite(fittedWidth) || fittedWidth <= 0 || !Number.isFinite(fittedHeight) || fittedHeight <= 0) {
    image.setDisplaySize(boundWidth, boundHeight);
    return;
  }
  image.setDisplaySize(fittedWidth, fittedHeight);
}

export class GameSession {
  private readonly options: GameSessionOptions;
  private readonly game: Phaser.Game;
  private readonly resizeObserver: ResizeObserver | null;
  private quality: VisualQuality;
  private scene: PlayScene | null = null;
  private pendingProjection: SceneProjection | null = null;
  private pendingHints: number[] = [];
  private pendingPhase: RoomPhase | null = null;
  private pendingFrozen = false;

  constructor(options: GameSessionOptions) {
    this.options = options;
    this.quality = options.quality;
    const renderScale = renderScaleForQuality(this.quality);
    const initialSize = outputSize(options.container, renderScale);
    const bindScene = (scene: PlayScene) => {
      this.scene = scene;
      if (this.pendingProjection) scene.applyProjection(this.pendingProjection);
      scene.setHintRegions(this.pendingHints);
      if (this.pendingPhase) scene.setPhase(this.pendingPhase);
      scene.setFrozen(this.pendingFrozen);
    };
    class SessionScene extends PlayScene {
      constructor() { super(options, bindScene); }
    }
    this.game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: options.container,
      backgroundColor: "#0b1724",
      scale: {
        mode: Phaser.Scale.NONE,
        width: initialSize.width,
        height: initialSize.height,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
      render: { antialias: this.quality !== "economy", roundPixels: true, pixelArt: false, powerPreference: this.quality === "economy" ? "low-power" : "high-performance" },
      input: { activePointers: 3, smoothFactor: 0 },
      scene: [SessionScene],
    });
    this.resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver((entries) => {
      const bounds = entries[0]?.contentRect;
      if (!bounds || bounds.width <= 0 || bounds.height <= 0) return;
      const scale = renderScaleForQuality(this.quality);
      const size = outputSize(options.container, scale);
      if (this.game.scale.width !== size.width || this.game.scale.height !== size.height) this.game.scale.resize(size.width, size.height);
      this.scene?.setRenderScale(scale);
    });
    this.resizeObserver?.observe(options.container);
  }

  applyProjection(projection: SceneProjection): void { this.pendingProjection = projection; this.scene?.applyProjection(projection); }
  setControl(control: GameControl, active: boolean): void { this.scene?.setControl(control, active); }
  setInteractHeld(active: boolean): void { this.scene?.setInteractHeld(active); }
  setFrozen(frozen: boolean): void { this.pendingFrozen = frozen; this.scene?.setFrozen(frozen); }
  setPhase(phase: RoomPhase): void { this.pendingPhase = phase; this.scene?.setPhase(phase); }
  setHintRegions(regions: number[]): void { this.pendingHints = regions; this.scene?.setHintRegions(regions); }
  setReducedMotion(reduced: boolean): void { this.scene?.setReducedMotion(reduced); }
  setVisualQuality(quality: VisualQuality): void {
    this.quality = quality;
    const scale = renderScaleForQuality(quality);
    const size = outputSize(this.options.container, scale);
    if (this.game.scale.width !== size.width || this.game.scale.height !== size.height) this.game.scale.resize(size.width, size.height);
    this.scene?.setVisualQuality(quality);
    this.scene?.setRenderScale(scale);
  }
  setMap(map: LoadedMap): void { this.scene?.setMap(map); }
  chooseNearestProp(): void { this.scene?.chooseNearestProp(); }
  scanNearestProp(): void { this.scene?.scanNearestProp(); }
  pushNearestActor(): void { this.scene?.pushNearestActor(); }
  areaBurst(): void { this.scene?.areaBurst(); }
  interactNearestBossTarget(): void { this.scene?.interactNearestBossTarget(); }
  setChallengeOption(optionId: string | null): void { this.scene?.setChallengeOption(optionId); }
  setReplay(samples: ReplaySample[] | null, nickname = ""): void { this.scene?.setReplay(samples, nickname); }

  destroy(): void {
    this.resizeObserver?.disconnect();
    this.game.destroy(true);
    this.scene = null;
  }
}

class PlayScene extends Phaser.Scene {
  private readonly options: GameSessionOptions;
  private loaded: LoadedMap;
  private activeArtifact: MapArtifact;
  private readonly mode: ActiveMode;
  private readonly networked: boolean;
  private reducedMotion: boolean;
  private visualQuality: VisualQuality;
  private readonly handlers: GameSessionOptions;
  private movement: MovementState;
  private rush: ReturnType<typeof createRushState> | null = null;
  private remoteSelf: OwnPlayerState | null = null;
  private projection: SceneProjection | null = null;
  private activeReplay: ReplaySample[] = [];
  private replayNickname = "";
  private arenaLayer!: Phaser.GameObjects.Container;
  private arenaEffects!: Phaser.GameObjects.Graphics;
  private dustGraphics!: Phaser.GameObjects.Graphics;
  private dustParticles: DustParticle[] = [];
  private dustClock = 0;
  private arenaSprites = new Map<string, Phaser.GameObjects.Image>();
  private practiceSprites = new Map<string, Phaser.GameObjects.Container>();
  private rebuildLabels = new Map<string, Phaser.GameObjects.Text>();
  private replayActor!: Phaser.GameObjects.Container;
  private replayLabel!: Phaser.GameObjects.Text;
  private keys: Record<string, Phaser.Input.Keyboard.Key> = {};
  private controls: Record<GameControl, boolean> = { left: false, right: false, jump: false, dash: false };
  private interactHeld = false;
  private interactRepeatMs = 0;
  private jumpEdge = false;
  private dashEdge = false;
  private sequence = 0;
  private pendingInputs: InputFrame[] = [];
  private outgoingInputs: InputFrame[] = [];
  private accumulator = 0;
  private frozen = false;
  private ended = false;
  private hudClock = 0;
  private lastAnnouncement = "";
  private readonly actorTracks = new Map<string, ActorTrack>();
  private readonly objectSprites = new Map<string, Phaser.GameObjects.Image>();
  private backgroundLayer!: Phaser.GameObjects.Container;
  private adaptationLayer!: Phaser.GameObjects.Container;
  private staticLayer!: Phaser.GameObjects.Graphics;
  private objectLayer!: Phaser.GameObjects.Container;
  private actorLayer!: Phaser.GameObjects.Container;
  private hintLayer!: Phaser.GameObjects.Graphics;
  private propSelectionLayer!: Phaser.GameObjects.Graphics;
  private ownActor!: Phaser.GameObjects.Container;
  private ownBody!: Phaser.GameObjects.Graphics;
  private ownAvatar!: Phaser.GameObjects.Sprite;
  private ownLabel!: Phaser.GameObjects.Text;
  private ownText = "";
  private hints: number[] = [];
  private actorHistory = new Map<string, VisibleActor>();
  private visualCorrection = { x: 0, y: 0 };
  private correctionTarget = { x: 0, y: 0 };
  private worldReady = false;
  private currentPhase: RoomPhase | undefined;
  private readonly avatarTexture = "__urlcade_avatar";

  constructor(options: GameSessionOptions, private readonly onScene: (scene: PlayScene) => void) {
    super({ key: "urlcade-play" });
    this.options = options;
    this.handlers = options;
    this.loaded = options.map;
    this.activeArtifact = options.challengeOptionId ? withChallengeSetup(options.map.artifact, options.challengeOptionId) : options.map.artifact;
    this.mode = options.mode;
    this.networked = options.networked;
    this.reducedMotion = options.reducedMotion;
    this.visualQuality = options.quality;
    this.activeReplay = options.replaySamples ?? [];
    this.replayNickname = options.replayNickname ?? "";
    const setup = this.activeArtifact.setup;
    const spawn = this.mode === "scroll_rush" && setup.mode === "scroll_rush" ? setup.spawn
      : setup.mode === "site_hide_seek" ? setup.seekerSpawn
      : setup.mode === "capture_logo" ? setup.teamSpawns.red[0] ?? { x: 80, y: 100 }
      : setup.mode === "page_brawl" || setup.mode === "browser_boss" ? setup.spawns[0] ?? { x: 80, y: 100 }
      : { x: 80, y: 100 };
    this.movement = createMovementState(spawn);
    if (this.mode === "scroll_rush" && this.activeArtifact.setup.mode === "scroll_rush") this.rush = createRushState(this.activeArtifact);
  }

  preload(): void {
    for (const [assetId, url] of this.loaded.assetUrls) {
      if (!this.textures.exists(assetId)) this.load.image(assetId, url);
    }
    for (const [id, asset] of Object.entries(characterAssets)) {
      if (!this.textures.exists(id)) this.load.spritesheet(id, asset.url, { frameWidth: asset.frameWidth, frameHeight: asset.frameHeight, endFrame: asset.columns * asset.rows - 1 });
    }
  }

  create(): void {
    this.onScene(this);
    this.onHud({ mode: this.mode, elapsedMs: 0, checkpoint: 0, penaltyMs: 0, announcement: tr.gameReadyAnnouncement });
    this.cameras.main.setBackgroundColor("#0b1724");
    this.cameras.main.setBounds(0, 0, this.activeArtifact.world.width, this.activeArtifact.world.height);
    this.setRenderScale(renderScaleForQuality(this.visualQuality));
    this.cameras.main.roundPixels = true;
    this.backgroundLayer = this.add.container(0, 0).setDepth(0);
    this.staticLayer = this.add.graphics().setDepth(1);
    this.hintLayer = this.add.graphics().setDepth(2);
    this.adaptationLayer = this.add.container(0, 0).setDepth(2.5);
    this.objectLayer = this.add.container(0, 0).setDepth(3);
    this.propSelectionLayer = this.add.graphics().setDepth(3.5);
    this.actorLayer = this.add.container(0, 0).setDepth(4);
    this.arenaLayer = this.add.container(0, 0).setDepth(5);
    this.arenaEffects = this.add.graphics().setDepth(6);
    this.dustGraphics = this.add.graphics().setDepth(7);
    this.createAvatarTexture();
    this.createCharacterAnimations();
    this.drawWorld();
    this.ownActor = this.add.container(0, 0).setDepth(8);
    const ownCharacter = characterFor(this.options.characterId);
    this.ownAvatar = this.add.sprite(0, 0, ownCharacter, 0).setOrigin(0.5, 1).setDisplaySize(44, 44);
    this.playCharacterPose(this.ownAvatar, ownCharacter, "idle");
    this.ownBody = this.add.graphics();
    this.ownLabel = this.add.text(0, -49, tr.avatarSelf, { fontFamily: "system-ui, sans-serif", fontSize: "12px", color: "#eaf6ff", fontStyle: "700", stroke: "#091321", strokeThickness: 4 }).setOrigin(0.5, 1);
    this.ownActor.add([this.ownAvatar, this.ownBody, this.ownLabel]);
    this.replayActor = this.add.container(0, 0).setDepth(7).setAlpha(0.48).setVisible(false);
    const replayBody = this.add.graphics();
    replayBody.fillStyle(0xff2bd6, 0.62).fillRoundedRect(-11, -34, 22, 34, 7);
    replayBody.lineStyle(2, 0xffb4ec, 0.95).strokeRoundedRect(-11, -34, 22, 34, 7);
    this.replayLabel = this.add.text(0, -38, "", { fontFamily: "system-ui, sans-serif", fontSize: "10px", color: "#ffe4f8", fontStyle: "700", stroke: "#091321", strokeThickness: 4 }).setOrigin(0.5, 1);
    this.replayActor.add([replayBody, this.replayLabel]);
    this.setReplay(this.activeReplay, this.replayNickname);
    this.drawOwnAvatar();
    this.createInput();
    this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => this.pointerAction(pointer));
    this.worldReady = true;
    this.applyProjection(this.projection ?? emptyProjection());
    // The first camera frame must use the current spawn/projection, before the
    // update loop has had a chance to position the actor.
    this.renderOwnActor();
    this.cameras.main.startFollow(this.ownActor, false, this.reducedMotion ? 1 : 0.14, this.reducedMotion ? 1 : 0.14);
    this.cameras.main.setDeadzone(140, 95);
  }

  update(_time: number, delta: number): void {
    if (!this.worldReady || this.ended) return;
    const safeDelta = Math.max(0, Math.min(delta, 100));
    this.accumulator += safeDelta;
    let stepped = 0;
    while (this.accumulator >= RULES.tickMs && stepped < 6) {
      this.fixedStep();
      this.accumulator -= RULES.tickMs;
      stepped++;
    }
    if (this.accumulator >= RULES.tickMs) this.accumulator = 0;
    this.interpolateActors();
    this.renderOwnActor();
    this.updateDust(safeDelta);
    this.hudClock += safeDelta;
    if (this.hudClock >= 80) {
      this.hudClock = 0;
      this.emitHud();
    }
    this.renderReplay();
    this.syncArena(this.projection?.arena);
  }

  setControl(control: GameControl, active: boolean): void {
    const wasActive = this.controls[control];
    this.controls[control] = active;
    if (active && !wasActive && control === "jump") this.jumpEdge = true;
    if (active && !wasActive && control === "dash") this.dashEdge = true;
  }

  setInteractHeld(active: boolean): void {
    const beganPress = active && !this.interactHeld;
    this.interactHeld = active;
    const bossPhase = this.projection?.arena?.mode === "browser_boss" ? this.projection.arena.boss?.phase : undefined;
    if (beganPress && ((this.mode === "browser_boss" && bossPhase === "switches") || this.mode === "capture_logo")) this.interactNearestBossTarget();
  }

  setFrozen(frozen: boolean): void {
    this.frozen = frozen;
    if (frozen) this.clearNetworkInputs();
    this.drawPropSelection();
  }
  setPhase(phase: RoomPhase): void {
    this.currentPhase = phase;
    if (!this.canMoveInCurrentPhase()) this.clearNetworkInputs();
    this.drawPropSelection();
    this.emitHud();
  }
  setReducedMotion(reduced: boolean): void {
    this.reducedMotion = reduced;
    this.cameras.main.setLerp(reduced ? 1 : 0.14, reduced ? 1 : 0.14);
    if (reduced) this.dustParticles = [];
  }

  setVisualQuality(quality: VisualQuality): void {
    this.visualQuality = quality;
    const budget = this.dustBudget();
    if (this.dustParticles.length > budget) this.dustParticles.length = budget;
  }

  setRenderScale(scale: number): void {
    this.cameras.main.setZoom(scale);
  }

  setMap(map: LoadedMap): void {
    if (map.artifact.artifactHash !== this.loaded.artifact.artifactHash) return;
    this.loaded = map;
    this.applyChallengeArtifact();
    this.drawWorld();
  }

  setChallengeOption(optionId: string | null): void {
    if ((this.options.challengeOptionId ?? null) === optionId) return;
    if (this.options.networked && this.currentPhase && this.currentPhase !== "lobby" && this.currentPhase !== "countdown") return;
    this.options.challengeOptionId = optionId;
    this.applyChallengeArtifact();
    if (this.worldReady) this.drawWorld();
  }

  setReplay(samples: ReplaySample[] | null, nickname = ""): void {
    this.activeReplay = (samples ?? []).slice(0, 1802);
    this.replayNickname = nickname;
    if (this.replayLabel) this.replayLabel.setText(nickname ? `${tr.ghostLabel} · ${nickname}` : tr.ghostLabel);
    this.renderReplay();
  }

  private applyChallengeArtifact(): void {
    const optionId = this.options.challengeOptionId;
    this.activeArtifact = optionId && this.loaded.artifact.setup.mode === "scroll_rush"
      ? withChallengeSetup(this.loaded.artifact, optionId)
      : this.loaded.artifact;
    if (this.mode === "scroll_rush" && this.activeArtifact.setup.mode === "scroll_rush") {
      this.rush = createRushState(this.activeArtifact);
      this.movement = this.rush.movement;
      this.clearNetworkInputs();
    }
  }

  setHintRegions(regions: number[]): void {
    this.hints = regions.filter((region) => Number.isInteger(region) && region >= 0 && region < 9);
    this.drawHints();
  }

  chooseNearestProp(): void {
    if (this.mode !== "site_hide_seek" || !this.networked || this.frozen || this.remoteSelf?.role !== "hider" || this.remoteSelf.caught || !this.projection?.self || !["hiding", "searching"].includes(this.currentPhase ?? "")) return;
    const origin = this.projection.self.movement;
    const bodyCenter = { x: origin.x, y: origin.y - 18 };
    const candidate = this.loaded.artifact.propTemplates.map((template) => ({ template, center: { x: template.sourceBounds.x + template.sourceBounds.width / 2, y: template.sourceBounds.y + template.sourceBounds.height / 2 } }))
      .map((item) => ({ ...item, distance: distance(bodyCenter, item.center) }))
      .filter((item) => item.distance <= RULES.scanRange)
      .sort((a, b) => a.distance - b.distance)[0];
    if (candidate) this.handlers.onAction("select_prop", candidate.template.id);
    else this.onHud({ ...this.currentHud(), announcement: tr.nearPropHelp });
  }

  scanNearestProp(): void {
    if (this.mode !== "site_hide_seek" || this.remoteSelf?.role !== "seeker" || this.remoteSelf.caught || this.currentPhase !== "searching" || !this.networked || this.frozen) return;
    const movement = this.remoteSelf.movement;
    const origin = { x: movement.x, y: movement.y - 18 };
    const props = this.projection?.objects ?? [];
    const candidates = props.map((object) => ({ point: { x: object.x, y: object.y - object.height / 2 } }));
    for (const target of this.projection?.practiceTargets ?? []) {
      if (target.kind === "prop" && target.active) candidates.push({ point: { x: target.x, y: target.y - target.height / 2 } });
    }
    const candidate = candidates
      .map((item) => ({ ...item, distance: distance(origin, item.point) }))
      .filter((item) => item.distance <= RULES.scanRange)
      .sort((a, b) => a.distance - b.distance)[0];
    if (candidate) this.handlers.onAction("scan", candidate.point);
    else this.onHud({ ...this.currentHud(), announcement: tr.noNearbyScanTarget });
  }

  pushNearestActor(): void {
    if (!this.networked || (this.mode !== "capture_logo" && this.mode !== "page_brawl") || this.currentPhase !== "playing" ||
      this.frozen || !this.remoteSelf || this.remoteSelf.caught || (this.remoteSelf.stunnedMs ?? 0) > 0 || (this.remoteSelf.actionCooldownMs ?? 0) > 0) return;
    const center = { x: this.remoteSelf.movement.x, y: this.remoteSelf.movement.y - 18 };
    const ownTeam = this.remoteSelf.team;
    const candidates = this.projection?.actors.map((actor) => {
      const team = this.projection?.arena?.players.find((player) => player.id === actor.id)?.team;
      return { actor, team, d: distance(center, { x: actor.x, y: actor.y - 18 }) };
    }) ?? [];
    if (this.mode === "page_brawl") {
      for (const target of this.projection?.practiceTargets ?? []) {
        if (target.kind !== "dummy" || !target.active) continue;
        const actor: VisibleActor = { id: target.id, x: target.x, y: target.y, nickname: tr.practiceDummy };
        candidates.push({ actor, team: undefined, d: distance(center, { x: target.x, y: target.y - 18 }) });
      }
    }
    const target = candidates.filter((item) => item.d <= EXT_RULES.pushRange && (this.mode !== "capture_logo" || item.team !== ownTeam))
      .sort((a, b) => a.d - b.d)[0];
    if (target) this.handlers.onAction("push_attack", { direction: target.actor.x < this.remoteSelf.movement.x ? -1 : 1, targetId: target.actor.id });
    else this.onHud({ ...this.currentHud(), announcement: tr.pushNoTarget });
  }

  areaBurst(): void {
    if (!this.networked || this.mode !== "page_brawl" || this.currentPhase !== "playing" || this.frozen || !this.remoteSelf ||
      this.remoteSelf.caught || (this.remoteSelf.stunnedMs ?? 0) > 0 || (this.remoteSelf.burstCooldownMs ?? 0) > 0) return;
    this.handlers.onAction("area_pulse");
  }

  interactNearestBossTarget(): void {
    if (!this.networked || this.currentPhase !== "playing" || this.frozen || !this.remoteSelf ||
      this.remoteSelf.caught || (this.remoteSelf.stunnedMs ?? 0) > 0 || (this.remoteSelf.actionCooldownMs ?? 0) > 0) return;
    if (this.mode === "capture_logo") {
      const logo = this.projection?.arena?.mode === "capture_logo" ? this.projection.arena.logo : undefined;
      if (logo?.carrierId === this.remoteSelf.id) {
        this.handlers.onAction("drop_carried");
        return;
      }
      if (logo && intersects(bodyBounds(this.remoteSelf.movement), { x: logo.x - 20, y: logo.y - 36, width: 40, height: 40 })) {
        this.handlers.onAction("interact", "logo");
        return;
      }
      this.onHud({ ...this.currentHud(), announcement: tr.logoTooFar });
      return;
    }
    if (this.mode !== "browser_boss") return;
    const setup = this.activeArtifact.setup;
    const boss = this.projection?.arena?.boss;
    if (setup.mode !== "browser_boss" || !boss) return;
    const center = { x: this.remoteSelf.movement.x, y: this.remoteSelf.movement.y - 18 };
    if (boss.phase === "weak" && intersects(bodyBounds(this.remoteSelf.movement), setup.weakPoint)) {
      this.handlers.onAction("interact", "weak_point");
      return;
    }
    const active = new Set(boss.activeSwitchIds);
    const nearest = setup.switches.filter((item) => !active.has(item.id)).map((item) => ({ item, center: { x: item.bounds.x + item.bounds.width / 2, y: item.bounds.y + item.bounds.height / 2 } }))
      .map((item) => ({ ...item, distance: distance(center, item.center) }))
      .filter((item) => item.distance <= EXT_RULES.bossInteractRange)
      .sort((a, b) => a.distance - b.distance)[0];
    if (nearest) this.handlers.onAction("interact", nearest.item.id);
    else this.onHud({ ...this.currentHud(), announcement: tr.bossNoTarget });
  }

  applyProjection(projection: SceneProjection): void {
    this.projection = projection;
    this.remoteSelf = projection.self;
    if (projection.self) {
      if (projection.self.caught) this.clearNetworkInputs();
      this.reconcile(projection.self);
    }
    this.syncObjects(projection.objects);
    this.syncActors(projection.actors);
    this.syncArena(projection.arena);
    this.syncPracticeTargets(projection.practiceTargets ?? []);
    this.drawHints();
    this.drawPropSelection();
    if (this.worldReady) this.emitHud();
  }

  private hasCountedOverlayObjective(map: MapArtifact, objectiveId: string, bounds?: Rect): boolean {
    if (map.schemaVersion !== 4) return false;
    const layer = map.visualLayers.find((item) => item.id === objectiveId && item.role === "objective" && item.origin !== "source" && item.adaptationId);
    if (!layer || (bounds && (layer.bounds.x !== bounds.x || layer.bounds.y !== bounds.y || layer.bounds.width !== bounds.width || layer.bounds.height !== bounds.height))) return false;
    const adaptation = map.adaptations.find((item) => item.id === layer.adaptationId);
    return Boolean(adaptation?.visualLayerIds.includes(layer.id) && adaptation.objectiveIds.includes(objectiveId));
  }

  private drawWorld(): void {
    this.backgroundLayer.removeAll(true);
    this.adaptationLayer.removeAll(true);
    this.staticLayer.clear();
    this.arenaLayer?.removeAll(true);
    this.arenaSprites.clear();
    this.practiceSprites.clear();
    this.rebuildLabels.clear();
    this.arenaEffects?.clear();
    const map = this.activeArtifact;
    const breakableIds = new Set(map.setup.mode === "page_brawl" ? map.setup.breakables.map((item) => item.id) : []);
    const disabledColliders = new Set<string>();
    if (map.setup.mode === "browser_boss" && this.projection?.arena?.mode === "browser_boss") {
      const active = new Set(this.projection.arena.activeColliderIds);
      map.colliders.forEach((collider) => { if (!active.has(collider.id)) disabledColliders.add(collider.id); });
    }
    for (const tile of map.backgroundTiles) {
      const key = tile.assetId;
      if (!this.textures.exists(key)) continue;
      const image = this.add.image(tile.bounds.x, tile.bounds.y, key).setOrigin(0, 0).setDisplaySize(tile.bounds.width, tile.bounds.height);
      this.backgroundLayer.add(image);
    }
    for (const collider of map.colliders) {
      if (disabledColliders.has(collider.id)) continue;
      const r = collider.bounds;
      if (collider.kind === "one_way") {
        this.staticLayer.lineStyle(2, 0x4ee0c1, 0.48).beginPath().moveTo(r.x, r.y).lineTo(r.x + r.width, r.y).strokePath();
        this.staticLayer.fillStyle(0x4ee0c1, 0.08).fillRect(r.x, r.y, r.width, Math.min(6, r.height));
      } else {
        this.staticLayer.lineStyle(1, 0x9cb6ce, 0.12).strokeRect(r.x, r.y, r.width, r.height);
      }
    }
    if (map.setup.mode === "scroll_rush") {
      map.setup.checkpoints.forEach((checkpoint, index) => {
        if (this.hasCountedOverlayObjective(map, `checkpoint_${index}`, checkpoint)) return;
        this.staticLayer.lineStyle(2, 0xffb568, 0.72).strokeRect(checkpoint.x, checkpoint.y, checkpoint.width, checkpoint.height);
        this.staticLayer.fillStyle(0xffb568, 0.1).fillRect(checkpoint.x, checkpoint.y, checkpoint.width, checkpoint.height);
        const marker = this.add.text(checkpoint.x + 8, checkpoint.y - 23, `CP 0${index + 1}`, { fontFamily: "system-ui, sans-serif", fontSize: "11px", color: "#ffd49b", fontStyle: "700", backgroundColor: "#172b40", padding: { x: 4, y: 2 } }).setDepth(6);
        this.backgroundLayer.add(marker);
      });
      const finish = map.setup.finish;
      if (!this.hasCountedOverlayObjective(map, "finish", finish)) {
        this.staticLayer.lineStyle(3, 0x4ee0c1, 0.88).strokeRect(finish.x, finish.y, finish.width, finish.height);
        this.staticLayer.fillStyle(0x4ee0c1, 0.15).fillRect(finish.x, finish.y, finish.width, finish.height);
        const finishText = this.add.text(finish.x + finish.width / 2, finish.y - 15, tr.finishLabel, { fontFamily: "system-ui, sans-serif", fontSize: "13px", color: "#c0fff2", fontStyle: "800", backgroundColor: "#0d342f", padding: { x: 7, y: 4 } }).setOrigin(0.5).setDepth(6);
        this.backgroundLayer.add(finishText);
      }
    }
    const modernLayers = map.schemaVersion === 4 ? map.visualLayers : undefined;
    for (const layer of map.visualLayers ?? []) {
      if (layer.id && breakableIds.has(layer.id)) continue;
      if ((map.setup.mode === "capture_logo" && layer.id === "logo") || (map.setup.mode === "browser_boss" && layer.id === "boss")) continue;
      if (!this.textures.exists(layer.assetId)) continue;
      const modernLayer = modernLayers?.find((item) => item.id === layer.id);
      if (modernLayer && modernLayer.origin !== "source") {
        const image = this.add.image(modernLayer.bounds.x + modernLayer.bounds.width / 2, modernLayer.bounds.y + modernLayer.bounds.height / 2, modernLayer.assetId).setOrigin(0.5);
        fitImageInsideDisplayBounds(image, modernLayer.bounds.width, modernLayer.bounds.height);
        this.adaptationLayer.add(image);
      } else {
        const image = this.add.image(layer.bounds.x, layer.bounds.y, layer.assetId).setOrigin(0, 0).setDisplaySize(layer.bounds.width, layer.bounds.height).setDepth(2);
        this.backgroundLayer.add(image);
      }
    }
    if (map.setup.mode === "page_brawl") {
      for (const breakable of map.setup.breakables) {
        const layer = map.visualLayers?.find((item) => item.id === breakable.id);
        if (!layer || !this.textures.exists(breakable.assetId)) continue;
        const modernLayer = map.schemaVersion === 4 ? map.visualLayers.find((item) => item.id === breakable.id) : undefined;
        const displayBounds = modernLayer?.bounds ?? layer.bounds;
        const adaptedOverlay = Boolean(modernLayer && modernLayer.origin !== "source");
        const image = adaptedOverlay
          ? this.add.image(displayBounds.x + displayBounds.width / 2, displayBounds.y + displayBounds.height / 2, breakable.assetId).setOrigin(0.5)
          : this.add.image(displayBounds.x, displayBounds.y, breakable.assetId).setOrigin(0, 0).setDisplaySize(displayBounds.width, displayBounds.height);
        if (adaptedOverlay) fitImageInsideDisplayBounds(image, displayBounds.width, displayBounds.height);
        this.arenaLayer.add(image);
        this.arenaSprites.set(breakable.id, image);
        const label = this.add.text(layer.bounds.x + layer.bounds.width / 2, layer.bounds.y - 8, "", { fontFamily: "system-ui, sans-serif", fontSize: "10px", color: "#fff0b8", fontStyle: "700", stroke: "#091321", strokeThickness: 4 }).setOrigin(0.5, 1).setVisible(false);
        this.arenaLayer.add(label);
        this.rebuildLabels.set(breakable.id, label);
      }
    }
    if (map.setup.mode === "capture_logo") {
      const setup = map.setup;
      const logoLayer = map.visualLayers?.find((item) => item.id === "logo" && item.assetId === setup.logoAssetId);
      const modernLogoLayer = map.schemaVersion === 4 ? map.visualLayers.find((item) => item.id === "logo" && item.assetId === setup.logoAssetId) : undefined;
      if (this.textures.exists(setup.logoAssetId)) {
        const image = this.add.image(setup.logoSpawn.x, setup.logoSpawn.y, setup.logoAssetId).setOrigin(0.5);
        const logoWidth = modernLogoLayer?.bounds.width ?? logoLayer?.bounds.width ?? 36;
        const logoHeight = modernLogoLayer?.bounds.height ?? logoLayer?.bounds.height ?? 36;
        if (modernLogoLayer && modernLogoLayer.origin !== "source") fitImageInsideDisplayBounds(image, logoWidth, logoHeight);
        else image.setDisplaySize(logoWidth, logoHeight);
        this.arenaLayer.add(image);
        this.arenaSprites.set("__logo", image);
      }
    }
    if (map.setup.mode === "browser_boss") {
      const setup = map.setup;
      const bossLayer = map.visualLayers?.find((item) => item.id === "boss" && item.assetId === setup.bossAssetId);
      const modernBossLayer = map.schemaVersion === 4 ? map.visualLayers.find((item) => item.id === "boss" && item.assetId === setup.bossAssetId) : undefined;
      if (this.textures.exists(setup.bossAssetId)) {
        const adaptedOverlay = Boolean(modernBossLayer && modernBossLayer.origin !== "source");
        const bossBounds = modernBossLayer && modernBossLayer.origin !== "source" ? modernBossLayer.bounds : setup.bossBounds;
        const image = adaptedOverlay
          ? this.add.image(bossBounds.x + bossBounds.width / 2, bossBounds.y + bossBounds.height / 2, setup.bossAssetId).setOrigin(0.5)
          : this.add.image(bossBounds.x, bossBounds.y, setup.bossAssetId).setOrigin(0, 0).setDisplaySize(bossBounds.width, bossBounds.height);
        if (adaptedOverlay) fitImageInsideDisplayBounds(image, bossBounds.width, bossBounds.height);
        this.arenaLayer.add(image);
        this.arenaSprites.set("__boss", image);
      }
    }
    if (map.setup.mode === "capture_logo") {
      for (const [team, zone] of Object.entries(map.setup.teamZones)) {
        const objectiveId = team === "red" ? "red_base" : "blue_base";
        if (this.hasCountedOverlayObjective(map, objectiveId, zone)) continue;
        const color = team === "red" ? 0xff647c : 0x5fa3ff;
        this.staticLayer.lineStyle(3, color, 0.78).strokeRect(zone.x, zone.y, zone.width, zone.height);
        this.staticLayer.fillStyle(color, 0.12).fillRect(zone.x, zone.y, zone.width, zone.height);
        const label = this.add.text(zone.x + 8, zone.y + 8, team === "red" ? tr.redTeam : tr.blueTeam, { fontFamily: "system-ui, sans-serif", fontSize: "11px", color: team === "red" ? "#ffc4cd" : "#c2dcff", fontStyle: "800", backgroundColor: "#091321", padding: { x: 5, y: 3 } }).setDepth(6);
        this.backgroundLayer.add(label);
      }
    }
    if (map.setup.mode === "browser_boss") {
      map.setup.switches.forEach((item, index) => {
        if (this.hasCountedOverlayObjective(map, item.id, item.bounds)) return;
        const label = this.add.text(item.bounds.x + item.bounds.width / 2, item.bounds.y - 8, `${tr.bossSwitches} ${index + 1}`, { fontFamily: "system-ui, sans-serif", fontSize: "10px", color: "#fff0b8", fontStyle: "700", stroke: "#091321", strokeThickness: 4 }).setOrigin(0.5, 1).setDepth(6);
        this.backgroundLayer.add(label);
      });
      if (!this.hasCountedOverlayObjective(map, "weak_point", map.setup.weakPoint)) this.staticLayer.lineStyle(2, 0xff2bd6, 0.44).strokeRect(map.setup.weakPoint.x, map.setup.weakPoint.y, map.setup.weakPoint.width, map.setup.weakPoint.height);
    }
    this.syncArena(this.projection?.arena);
  }

  private collisionMap(): Pick<MapArtifact, "world" | "colliders"> {
    const arena = this.projection?.arena;
    if (!arena) return this.activeArtifact;
    const activeIds = new Set(arena.activeColliderIds);
    const disabled = new Set(this.activeArtifact.colliders.filter((collider) => !activeIds.has(collider.id)).map((collider) => collider.id));
    return { world: this.activeArtifact.world, colliders: activeColliders(this.activeArtifact, disabled) };
  }

  private movementOptions(): Parameters<typeof stepMovement>[4] {
    const self = this.remoteSelf;
    const carryingLogo = this.mode === "capture_logo" && this.projection?.arena?.mode === "capture_logo" && this.projection.arena.logo?.carrierId === self?.id;
    return {
      mode: this.mode,
      seeker: self?.role === "seeker",
      speedScale: carryingLogo ? EXT_RULES.logoCarrierSpeedScale : 1,
      ...(this.mode === "capture_logo" ? { dashAllowed: !carryingLogo } : {}),
      horizontalBounds: this.mode !== "page_brawl",
    };
  }

  private syncArena(arena?: ArenaProjection): void {
    if (!this.arenaLayer || !this.arenaEffects) return;
    this.arenaEffects.clear();
    if (this.mode === "page_brawl" && this.activeArtifact.setup.mode === "page_brawl") {
      for (const item of this.activeArtifact.setup.breakables) {
        const sprite = this.arenaSprites.get(item.id);
        const label = this.rebuildLabels.get(item.id);
        const state = arena?.mode === "page_brawl" ? arena.breakables.find((entry) => entry.id === item.id) : undefined;
        const visible = state?.active ?? true;
        sprite?.setVisible(visible);
        label?.setVisible(!visible);
        if (label && state && !state.active) label.setText(`${tr.breakableRebuild} · ${Math.ceil(state.rebuildRemainingMs / 1000)}s`);
      }
    }
    if (this.mode === "capture_logo") {
      const logo = arena?.mode === "capture_logo" ? arena.logo : undefined;
      const sprite = this.arenaSprites.get("__logo");
      if (sprite) {
        sprite.setVisible(true);
        if (logo) {
          sprite.setPosition(logo.x, logo.y);
          sprite.setTint(logo.state === "carried" ? (logo.carrierId && arena?.players.find((player) => player.id === logo.carrierId)?.team === "red" ? 0xff8796 : 0x9dc6ff) : 0xffffff);
        }
      }
    }
    if (this.mode === "browser_boss" && this.activeArtifact.setup.mode === "browser_boss") {
      const setup = this.activeArtifact.setup;
      const boss = arena?.mode === "browser_boss" ? arena.boss : undefined;
      const sprite = this.arenaSprites.get("__boss");
      sprite?.setTint(boss?.phase === "weak" ? 0xffb5e9 : 0xffffff);
      if (boss) {
        const activated = new Set(boss.activeSwitchIds);
        setup.switches.forEach((item) => {
          const centerX = item.bounds.x + item.bounds.width / 2;
          const centerY = item.bounds.y + item.bounds.height / 2;
          const on = activated.has(item.id);
          this.arenaEffects.fillStyle(on ? 0x4ee0c1 : 0xffd400, on ? 0.38 : 0.22).fillCircle(centerX, centerY, Math.max(14, Math.min(item.bounds.width, item.bounds.height) / 2));
          this.arenaEffects.lineStyle(2, on ? 0x8ffff0 : 0xffed93, 0.92).strokeCircle(centerX, centerY, Math.max(14, Math.min(item.bounds.width, item.bounds.height) / 2));
        });
        if (boss.phase === "weak") {
          const weak = setup.weakPoint;
          this.arenaEffects.fillStyle(0xff2bd6, 0.32).fillRect(weak.x, weak.y, weak.width, weak.height);
          this.arenaEffects.lineStyle(4, 0xffb4ec, 0.98).strokeRect(weak.x, weak.y, weak.width, weak.height);
        }
        if (boss.telegraph && boss.telegraphRemainingMs > 0) {
          this.arenaEffects.fillStyle(0xff445c, 0.28).fillRect(boss.telegraph.x, boss.telegraph.y, boss.telegraph.width, boss.telegraph.height);
          this.arenaEffects.lineStyle(3, 0xff647c, 0.92).strokeRect(boss.telegraph.x, boss.telegraph.y, boss.telegraph.width, boss.telegraph.height);
        }
      }
    }
  }

  private syncPracticeTargets(targets: PracticeTarget[]): void {
    if (!this.worldReady || !this.arenaLayer || !this.objectLayer) return;
    const visible = targets.filter((target) => target.active &&
      (this.mode === "page_brawl" ? target.kind === "dummy" : this.mode === "site_hide_seek" && target.kind === "prop"));
    const alive = new Set(visible.map((target) => target.id));
    for (const [id, sprite] of this.practiceSprites) {
      if (!alive.has(id)) {
        sprite.destroy(true);
        this.practiceSprites.delete(id);
      }
    }
    for (const target of visible) {
      let sprite = this.practiceSprites.get(target.id);
      if (!sprite) {
        sprite = this.add.container(target.x, target.y).setDepth(target.kind === "prop" ? 3 : 7);
        if (target.kind === "prop" && this.textures.exists(target.assetId)) {
          const image = this.add.image(0, 0, target.assetId).setOrigin(0.5, 1);
          fitImageInsideDisplayBounds(image, target.width, target.height);
          sprite.add(image);
          this.objectLayer.add(sprite);
        } else {
          const body = this.add.graphics();
          body.fillStyle(0xffb568, 0.95).fillRoundedRect(-target.width / 2, -target.height, target.width, target.height, 6);
          body.lineStyle(2, 0xffebc8, 0.9).strokeRoundedRect(-target.width / 2, -target.height, target.width, target.height, 6);
          const label = this.add.text(0, -target.height - 8, tr.practiceDummy, { fontFamily: "system-ui, sans-serif", fontSize: "10px", color: "#fff0cf", fontStyle: "700", stroke: "#091321", strokeThickness: 4 }).setOrigin(0.5, 1);
          sprite.add([body, label]);
          this.arenaLayer.add(sprite);
        }
        this.practiceSprites.set(target.id, sprite);
      }
      sprite.setPosition(target.x, target.y);
    }
  }

  private renderReplay(): void {
    if (!this.replayActor || this.mode !== "scroll_rush" || this.activeReplay.length === 0) {
      this.replayActor?.setVisible(false);
      return;
    }
    const elapsed = this.remoteSelf?.elapsedMs ?? this.rush?.elapsedMs ?? 0;
    const first = this.activeReplay[0];
    const last = this.activeReplay[this.activeReplay.length - 1];
    if (!first || !last || elapsed < first.t || elapsed > last.t) {
      this.replayActor.setVisible(false);
      return;
    }
    let index = 0;
    while (index + 1 < this.activeReplay.length && this.activeReplay[index + 1]!.t < elapsed) index++;
    const a = this.activeReplay[index]!;
    const b = this.activeReplay[Math.min(index + 1, this.activeReplay.length - 1)]!;
    const ratio = b.t === a.t ? 0 : Math.max(0, Math.min(1, (elapsed - a.t) / (b.t - a.t)));
    this.replayActor.setPosition(Phaser.Math.Linear(a.x, b.x, ratio), Phaser.Math.Linear(a.y, b.y, ratio)).setVisible(true);
  }

  private createInput(): void {
    if (!this.input.keyboard) return;
    const codes = Phaser.Input.Keyboard.KeyCodes;
    this.keys = {
      left: this.input.keyboard.addKey(codes.LEFT),
      right: this.input.keyboard.addKey(codes.RIGHT),
      a: this.input.keyboard.addKey(codes.A),
      d: this.input.keyboard.addKey(codes.D),
      jump: this.input.keyboard.addKey(codes.SPACE),
      dash: this.input.keyboard.addKey(codes.SHIFT),
      select: this.input.keyboard.addKey(codes.E),
      disguise: this.input.keyboard.addKey(codes.F),
      push: this.input.keyboard.addKey(codes.Q),
      burst: this.input.keyboard.addKey(codes.R),
    };
  }

  private fixedStep(): void {
    this.interactRepeatMs = Math.max(0, this.interactRepeatMs - RULES.tickMs);
    if (this.frozen || this.remoteSelf?.caught) {
      if (this.networked) this.clearNetworkInputs();
      return;
    }
    if (this.networked && !this.canMoveInCurrentPhase()) {
      this.clearNetworkInputs();
      return;
    }
    const input = this.readInput();
    const frame: InputFrame = {
      sequence: this.sequence++,
      axisX: input.axisX,
      jumpPressed: input.jumpPressed,
      dashPressed: (this.mode === "scroll_rush" || this.mode === "capture_logo") && input.dashPressed,
    };
    if (this.networked) {
      const maskedFrame = this.remoteSelf && (this.remoteSelf.stunnedMs ?? 0) > 0
        ? { ...frame, axisX: 0 as const, jumpPressed: false, dashPressed: false }
        : frame;
      this.pendingInputs.push(maskedFrame);
      if (this.pendingInputs.length > 120) this.pendingInputs.splice(0, this.pendingInputs.length - 120);
      this.movement = stepMovement(this.movement, maskedFrame, this.collisionMap(), RULES.tickMs, this.movementOptions());
      this.outgoingInputs.push(maskedFrame);
      if (this.outgoingInputs.length >= 3) this.flushInputs();
    } else if (this.mode === "scroll_rush" && this.rush) {
      this.rush = stepRush(this.rush, frame, this.activeArtifact, RULES.tickMs);
      this.movement = this.rush.movement;
      if (this.rush.finished) this.finishLocal(true);
      else if (this.activeArtifact.setup.mode === "scroll_rush" && this.rush.elapsedMs >= this.activeArtifact.setup.timeLimitMs) this.finishLocal(false);
    } else {
      this.movement = stepMovement(this.movement, frame, this.collisionMap(), RULES.tickMs, this.movementOptions());
    }
    this.readDiscreteActions();
    this.jumpEdge = false;
    this.dashEdge = false;
  }

  private readInput(): { axisX: -1 | 0 | 1; jumpPressed: boolean; dashPressed: boolean } {
    const justDown = this.input.keyboard ? Phaser.Input.Keyboard.JustDown : null;
    const leftKey = this.keys.left?.isDown || this.keys.a?.isDown || this.controls.left;
    const rightKey = this.keys.right?.isDown || this.keys.d?.isDown || this.controls.right;
    const jumpKey = Boolean(this.keys.jump && justDown?.(this.keys.jump));
    const dashKey = Boolean(this.keys.dash && justDown?.(this.keys.dash));
    return {
      axisX: leftKey === rightKey ? 0 : leftKey ? -1 : 1,
      jumpPressed: this.jumpEdge || jumpKey,
      dashPressed: this.dashEdge || dashKey,
    };
  }

  private readDiscreteActions(): void {
    if (!this.input.keyboard || this.networked === false) return;
    const justDown = Phaser.Input.Keyboard.JustDown;
    if (this.mode === "site_hide_seek") {
      const selectPressed = Boolean(this.keys.select && justDown(this.keys.select));
      const disguisePressed = Boolean(this.keys.disguise && justDown(this.keys.disguise));
      const focused = document.activeElement;
      if (!document.hasFocus() || focused instanceof HTMLElement && (focused.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(focused.tagName))) return;
      if (selectPressed) this.chooseNearestProp();
      if (disguisePressed && !this.frozen && this.remoteSelf?.role === "hider" && !this.remoteSelf.caught && ["hiding", "searching"].includes(this.currentPhase ?? "")) this.handlers.onAction("toggle_disguise");
    } else if (this.mode === "capture_logo" || this.mode === "page_brawl") {
      if (this.keys.push && justDown(this.keys.push)) this.pushNearestActor();
      if (this.mode === "capture_logo" && this.keys.select && justDown(this.keys.select)) this.interactNearestBossTarget();
      if (this.mode === "page_brawl" && this.keys.burst && justDown(this.keys.burst)) this.areaBurst();
    } else if (this.mode === "browser_boss" && this.keys.select) {
      const weak = this.projection?.arena?.mode === "browser_boss" && this.projection.arena.boss?.phase === "weak";
      if (weak) {
        if ((this.keys.select.isDown || this.interactHeld) && this.interactRepeatMs <= 0) {
          this.interactNearestBossTarget();
          this.interactRepeatMs = 250;
        }
      } else if (justDown(this.keys.select)) this.interactNearestBossTarget();
    }
  }

  private flushInputs(): void {
    if (this.outgoingInputs.length === 0) return;
    if (this.networked && (this.frozen || !this.canMoveInCurrentPhase())) {
      this.clearNetworkInputs();
      return;
    }
    const frames = this.outgoingInputs.splice(0, 6);
    this.handlers.onInput(frames);
  }

  private reconcile(self: OwnPlayerState): void {
    const prior = { ...this.movement };
    this.sequence = Math.max(this.sequence, self.sequence + 1);
    this.pendingInputs = this.pendingInputs.filter((frame) => frame.sequence > self.sequence);
    if (this.mode === "scroll_rush" && this.activeArtifact.setup.mode === "scroll_rush") {
      const start = { movement: self.movement, checkpoint: self.checkpoint, penaltyMs: self.penaltyMs, elapsedMs: self.elapsedMs, finished: self.finished };
      this.rush = this.pendingInputs.reduce((state, frame) => stepRush(state, frame, this.activeArtifact, RULES.tickMs), start);
      this.movement = this.rush.movement;
    } else {
      this.remoteSelf = self;
      this.movement = this.pendingInputs.reduce((state, frame) => stepMovement(state, frame, this.collisionMap(), RULES.tickMs, this.movementOptions()), self.movement);
    }
    const dx = prior.x - this.movement.x;
    const dy = prior.y - this.movement.y;
    if (Math.hypot(dx, dy) > 150) {
      this.visualCorrection = { x: 0, y: 0 };
      this.correctionTarget = { x: 0, y: 0 };
    } else {
      this.visualCorrection = { x: dx, y: dy };
      this.correctionTarget = { x: 0, y: 0 };
    }
    this.ownText = tr.avatarSelf;
    this.drawOwnAvatar();
  }

  private drawOwnAvatar(): void {
    if (!this.ownBody) return;
    this.ownBody.clear();
    const role = this.remoteSelf?.role;
    const disguised = this.mode === "site_hide_seek" && role === "hider" && this.remoteSelf?.disguised && Boolean(this.remoteSelf?.renderObjectId);
    if (disguised || this.remoteSelf?.caught) {
      this.ownActor?.setVisible(false);
      return;
    }
    this.ownActor?.setVisible(true);
    const color = this.mode === "capture_logo" && this.remoteSelf?.team ? this.remoteSelf.team === "red" ? 0xff647c : 0x5fa3ff : role === "seeker" ? 0xffb568 : 0x4ee0c1;
    this.ownBody.lineStyle(this.remoteSelf?.spawnProtectionMs ? 3 : 2, this.remoteSelf?.spawnProtectionMs ? 0xffd400 : color, 0.96).strokeRoundedRect(-12, -36, 24, 36, 8);
    this.ownAvatar?.setFlipX(this.movement.facing < 0);
    this.ownLabel.setText(this.ownText || tr.avatarSelf);
  }

  private dustBudget(): number {
    if (this.reducedMotion) return 0;
    return this.visualQuality === "high" ? 12 : this.visualQuality === "balanced" ? 6 : 0;
  }

  private updateDust(delta: number): void {
    if (!this.dustGraphics) return;
    const budget = this.dustBudget();
    this.dustClock += delta;
    if (budget > 0 && !this.frozen && !this.remoteSelf?.caught && this.movement.grounded && Math.abs(this.movement.vx) > 80 && this.dustClock >= (this.visualQuality === "high" ? 68 : 110)) {
      this.dustClock = 0;
      const offset = this.movement.facing > 0 ? -9 : 9;
      this.dustParticles.push({ x: this.movement.x + offset, y: this.movement.y - 1, vx: offset > 0 ? 10 : -10, vy: -8, lifeMs: 300, maxLifeMs: 300 });
      if (this.dustParticles.length > budget) this.dustParticles.splice(0, this.dustParticles.length - budget);
    }
    this.dustParticles = this.dustParticles.filter((particle) => {
      particle.lifeMs -= delta;
      particle.x += particle.vx * delta / 1000;
      particle.y += particle.vy * delta / 1000;
      particle.vy += 22 * delta / 1000;
      return particle.lifeMs > 0;
    });
    this.dustGraphics.clear();
    for (const particle of this.dustParticles) {
      this.dustGraphics.fillStyle(0x8ba4b9, Math.max(0, particle.lifeMs / particle.maxLifeMs) * 0.42).fillCircle(particle.x, particle.y, 1.8);
    }
  }

  private renderOwnActor(): void {
    if (!this.ownActor) return;
    const correction = this.visualCorrection;
    correction.x += (this.correctionTarget.x - correction.x) * (this.reducedMotion ? 1 : 0.18);
    correction.y += (this.correctionTarget.y - correction.y) * (this.reducedMotion ? 1 : 0.18);
    this.ownActor.setPosition(this.movement.x + correction.x, this.movement.y);
    const character = characterFor(this.options.characterId);
    const carrying = this.mode === "capture_logo" && this.projection?.arena?.mode === "capture_logo" && this.projection.arena.logo?.carrierId === this.remoteSelf?.id;
    const pose = (this.remoteSelf?.stunnedMs ?? 0) > 0 ? "hit"
      : carrying ? "carry"
      : this.mode === "site_hide_seek" && this.remoteSelf?.role === "hider" && this.remoteSelf.disguised ? "hide"
      : this.movement.dashMs > 0 ? "dash"
      : !this.movement.grounded ? this.movement.vy > 80 ? "fall" : "jump"
      : Math.abs(this.movement.vx) > 8 ? "run" : "idle";
    this.playCharacterPose(this.ownAvatar, character, pose);
    this.ownAvatar.setFlipX(this.movement.facing < 0);
    if (this.remoteSelf?.role === "hider" && this.remoteSelf.disguised && this.remoteSelf.renderObjectId) this.ownActor.setVisible(false);
    else if (!this.remoteSelf?.caught) this.ownActor.setVisible(true);
  }

  private syncObjects(objects: PresentationObject[]): void {
    if (!this.objectLayer) return;
    const drawObjects = this.remoteSelf?.renderObjectId && !this.remoteSelf.disguised
      ? objects.filter((object) => object.id !== this.remoteSelf?.renderObjectId)
      : objects;
    const alive = new Set(drawObjects.map((object) => object.id));
    for (const [id, image] of this.objectSprites) {
      if (!alive.has(id)) {
        image.destroy();
        this.objectSprites.delete(id);
      }
    }
    for (const object of drawObjects) {
      let image = this.objectSprites.get(object.id);
      const texture = this.textures.exists(object.assetId) ? object.assetId : this.avatarTexture;
      if (!image) {
        image = this.add.image(object.x, object.y, texture).setOrigin(0.5, 1).setAlpha(0.94);
        this.objectLayer.add(image);
        this.objectSprites.set(object.id, image);
      }
      if (image.texture.key !== texture) image.setTexture(texture);
      image.setOrigin(0.5, 1).setPosition(object.x, object.y);
      fitImageInsideDisplayBounds(image, object.width, object.height);
      // The protocol intentionally exposes only anonymous visual IDs here.
    }
  }

  private syncActors(actors: VisibleActor[]): void {
    if (!this.actorLayer) return;
    const visible = new Set(actors.map((actor) => actor.id));
    for (const [id, track] of this.actorTracks) {
      if (!visible.has(id)) {
        track.sprite.destroy(true);
        track.name.destroy();
        this.actorTracks.delete(id);
        this.actorHistory.delete(id);
      }
    }
    const now = performance.now();
    for (const actor of actors) {
      let track = this.actorTracks.get(actor.id);
      if (!track) {
        const sprite = this.add.container(actor.x, actor.y);
        const characterId = characterFor(actor.characterId);
        const avatar = this.add.sprite(0, 0, characterId, 0).setOrigin(0.5, 1).setDisplaySize(42, 42);
        this.playCharacterPose(avatar, characterId, "idle");
        const shape = this.add.graphics();
        const team = this.projection?.arena?.players.find((item) => item.id === actor.id)?.team;
        const color = this.mode === "capture_logo" && team ? team === "red" ? 0xff647c : 0x5fa3ff : 0x5fa3ff;
        shape.lineStyle(2, color, 0.96).strokeRoundedRect(-12, -36, 24, 36, 8);
        const label = this.add.text(0, -41, actor.nickname, { fontFamily: "system-ui, sans-serif", fontSize: "11px", color: "#eaf3ff", fontStyle: "700", stroke: "#091321", strokeThickness: 4 }).setOrigin(0.5, 1);
        sprite.add([avatar, shape]);
        this.actorLayer.add(sprite);
        this.actorLayer.add(label);
        track = { sprite, body: shape, avatar, characterId, name: label, fromX: actor.x, fromY: actor.y, toX: actor.x, toY: actor.y, changedAt: now };
        this.actorTracks.set(actor.id, track);
      } else {
        const characterId = characterFor(actor.characterId);
        if (track.characterId !== characterId) {
          track.characterId = characterId;
          track.avatar.setTexture(characterId, 0);
          this.playCharacterPose(track.avatar, characterId, "idle");
        }
        track.fromX = track.sprite.x;
        track.fromY = track.sprite.y;
        track.toX = actor.x;
        track.toY = actor.y;
        track.changedAt = now;
        track.name.setText(actor.nickname);
        const team = this.projection?.arena?.players.find((item) => item.id === actor.id)?.team;
        const color = this.mode === "capture_logo" && team ? team === "red" ? 0xff647c : 0x5fa3ff : 0x5fa3ff;
        track.body.clear().lineStyle(2, color, 0.96).strokeRoundedRect(-12, -36, 24, 36, 8);
      }
      this.actorHistory.set(actor.id, actor);
    }
  }

  private interpolateActors(): void {
    const now = performance.now();
    for (const track of this.actorTracks.values()) {
      const progress = Math.max(0, Math.min(1, (now - track.changedAt) / 100));
      track.sprite.setPosition(Phaser.Math.Linear(track.fromX, track.toX, progress), Phaser.Math.Linear(track.fromY, track.toY, progress));
      track.name.setPosition(track.sprite.x, track.sprite.y - 41);
      const dx = track.toX - track.fromX;
      const dy = track.toY - track.fromY;
      const pose = Math.abs(dy) > 3 ? dy < 0 ? "jump" : "fall" : Math.abs(dx) > 3 ? "run" : "idle";
      track.avatar.setFlipX(dx < -1);
      this.playCharacterPose(track.avatar, track.characterId, pose);
    }
  }

  private drawHints(): void {
    if (!this.hintLayer) return;
    this.hintLayer.clear();
    if (this.mode !== "site_hide_seek" || this.hints.length === 0) return;
    const cellW = this.loaded.artifact.world.width / 3;
    const cellH = this.loaded.artifact.world.height / 3;
    for (const index of this.hints) {
      const x = (index % 3) * cellW;
      const y = Math.floor(index / 3) * cellH;
      this.hintLayer.fillStyle(0xffb568, 0.055).fillRect(x, y, cellW, cellH);
      this.hintLayer.lineStyle(2, 0xffb568, 0.45).strokeRect(x + 3, y + 3, cellW - 6, cellH - 6);
    }
  }

  private drawPropSelection(): void {
    if (!this.propSelectionLayer) return;
    this.propSelectionLayer.clear();
    const self = this.remoteSelf;
    if (this.mode !== "site_hide_seek" || !this.networked || this.frozen || self?.role !== "hider" || self.caught || !["hiding", "searching"].includes(this.currentPhase ?? "")) return;
    const origin = { x: self.movement.x, y: self.movement.y - 18 };
    for (const template of this.loaded.artifact.propTemplates) {
      const bounds = template.sourceBounds;
      const nearby = distance(origin, { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 }) <= RULES.scanRange;
      const selected = template.id === self.selectedTemplateId;
      this.propSelectionLayer.lineStyle(selected ? 3 : nearby ? 2 : 1, selected ? 0xffd400 : 0x4ee0c1, selected ? 0.95 : nearby ? 0.85 : 0.2)
        .strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
    }
  }

  private pointerAction(pointer: Phaser.Input.Pointer): void {
    if (this.mode !== "site_hide_seek" || !this.networked || !this.remoteSelf || this.remoteSelf.caught || this.frozen) return;
    const point = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
    if (this.remoteSelf.role === "hider" && ["hiding", "searching"].includes(this.currentPhase ?? "")) {
      const origin = { x: this.remoteSelf.movement.x, y: this.remoteSelf.movement.y - 18 };
      const candidate = this.loaded.artifact.propTemplates.filter((template) => {
        const bounds = template.sourceBounds;
        return point.x >= bounds.x && point.x <= bounds.x + bounds.width && point.y >= bounds.y && point.y <= bounds.y + bounds.height;
      }).map((template) => ({ template, distance: distance(origin, { x: template.sourceBounds.x + template.sourceBounds.width / 2, y: template.sourceBounds.y + template.sourceBounds.height / 2 }) }))
        .filter((item) => item.distance <= RULES.scanRange)
        .sort((a, b) => a.distance - b.distance || a.template.id.localeCompare(b.template.id))[0];
      if (candidate) this.handlers.onAction("select_prop", candidate.template.id);
      else this.onHud({ ...this.currentHud(), announcement: tr.nearPropHelp });
      return;
    }
    if (this.remoteSelf.role === "seeker" && this.currentPhase === "searching") this.handlers.onAction("scan", { x: point.x, y: point.y });
  }

  private createAvatarTexture(): void {
    if (this.textures.exists(this.avatarTexture)) return;
    const avatar = this.make.graphics({ x: 0, y: 0 });
    avatar.fillStyle(0x4ee0c1, 1).fillRoundedRect(0, 0, 24, 36, 8);
    avatar.lineStyle(2, 0xe9fff9, 0.8).strokeRoundedRect(0, 0, 24, 36, 8);
    avatar.fillStyle(0x092018, 1).fillCircle(17, 11, 1.8);
    avatar.generateTexture(this.avatarTexture, 24, 36);
    avatar.destroy();
  }

  private createCharacterAnimations(): void {
    for (const id of Object.keys(characterAssets) as CharacterId[]) {
      for (const [pose, animation] of Object.entries(characterAssets[id].animations)) {
        if (animation.fallback) continue;
        const key = characterAnimationKey(id, pose as CharacterPose);
        if (!this.anims.exists(key)) this.anims.create({ key, frames: animation.frames.map((frame) => ({ key: id, frame })), frameRate: animation.fps, repeat: animation.loop ? -1 : 0 });
      }
    }
  }

  private playCharacterPose(sprite: Phaser.GameObjects.Sprite, id: CharacterId, pose: CharacterPose): void {
    if (this.reducedMotion && pose !== "idle" && pose !== "hide") pose = "idle";
    const key = characterAnimationKey(id, pose);
    if (this.anims.exists(key) && sprite.anims.currentAnim?.key !== key) sprite.play(key, true);
  }

  private canMoveInCurrentPhase(): boolean {
    if (!this.networked) return true;
    if (this.mode === "scroll_rush") return this.currentPhase === "racing";
    if (this.mode === "capture_logo") return this.currentPhase === "playing" && (this.remoteSelf?.role === "teammate");
    if (this.mode === "page_brawl") return this.currentPhase === "playing" && this.remoteSelf?.role === "fighter";
    if (this.mode === "browser_boss") return this.currentPhase === "playing" && this.remoteSelf?.role === "cooperator";
    if (this.currentPhase === "hiding") return this.remoteSelf?.role === "hider";
    if (this.currentPhase === "searching") return this.remoteSelf?.role === "hider" || this.remoteSelf?.role === "seeker";
    return false;
  }

  private clearNetworkInputs(): void {
    this.pendingInputs.length = 0;
    this.outgoingInputs.length = 0;
    this.controls.left = false;
    this.controls.right = false;
    this.controls.jump = false;
    this.controls.dash = false;
    this.interactHeld = false;
    this.interactRepeatMs = 0;
    this.jumpEdge = false;
    this.dashEdge = false;
    this.accumulator = 0;
  }

  private emitHud(): void {
    const previous = this.currentHud();
    const phase = this.currentPhase;
    const state = this.remoteSelf;
    const hud: GameHud = {
      ...previous,
      phase,
      elapsedMs: this.rush?.elapsedMs ?? state?.elapsedMs ?? previous.elapsedMs,
      checkpoint: this.rush?.checkpoint ?? state?.checkpoint ?? previous.checkpoint,
      penaltyMs: this.rush?.penaltyMs ?? state?.penaltyMs ?? previous.penaltyMs,
      role: state?.role,
      energy: state?.energy,
      disguised: state?.disguised,
      team: state?.team,
      stunnedMs: state?.stunnedMs,
      actionCooldownMs: state?.actionCooldownMs,
      burstCooldownMs: state?.burstCooldownMs,
      arena: this.projection?.arena,
      announcement: this.lastAnnouncement || undefined,
    };
    this.handlers.onHud(hud);
    this.lastAnnouncement = "";
  }

  private currentHud(): GameHud {
    return { mode: this.mode, elapsedMs: this.rush?.elapsedMs ?? this.remoteSelf?.elapsedMs ?? 0, checkpoint: this.rush?.checkpoint ?? this.remoteSelf?.checkpoint ?? 0, penaltyMs: this.rush?.penaltyMs ?? this.remoteSelf?.penaltyMs ?? 0, role: this.remoteSelf?.role, energy: this.remoteSelf?.energy, disguised: this.remoteSelf?.disguised, team: this.remoteSelf?.team, stunnedMs: this.remoteSelf?.stunnedMs, actionCooldownMs: this.remoteSelf?.actionCooldownMs, burstCooldownMs: this.remoteSelf?.burstCooldownMs, arena: this.projection?.arena };
  }

  private onHud(hud: GameHud): void {
    if (hud.announcement) this.lastAnnouncement = hud.announcement;
    this.handlers.onHud(hud);
  }

  private finishLocal(finished: boolean): void {
    if (this.networked || !this.rush || this.ended) return;
    this.ended = true;
    this.handlers.onLocalFinish({
      mapTitle: this.loaded.artifact.title,
      artifactHash: this.loaded.artifact.artifactHash,
      simulationVersion: this.loaded.artifact.simulationVersion,
      elapsedMs: this.rush.elapsedMs,
      penaltyMs: this.rush.penaltyMs,
      checkpoint: this.rush.checkpoint,
      finished,
    });
  }
}

function emptyProjection(): SceneProjection {
  return { tick: 0, objects: [], actors: [], self: null };
}
